import { useCallback, useEffect, useRef, useState } from 'react';

import { messageErreurRapport } from '../../lib/fleetReportsApi';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';

/**
 * Charge un état à chaque changement de `cle`, sans jamais afficher la réponse
 * d'une demande périmée (on change de mois plus vite que la réponse n'arrive).
 * `charger` doit être stable pour une même `cle`; il est lu au moment du chargement.
 */
export const useFleetReport = (cle, charger, { actif = true } = {}) => {
  const [etat, setEtat] = useState({ data: null, chargement: actif, erreur: null });
  const chargerRef = useRef(charger);
  chargerRef.current = charger;
  const numero = useRef(0);

  const lancer = useCallback(async ({ silencieux = false } = {}) => {
    numero.current += 1;
    const mien = numero.current;
    if (!silencieux) setEtat((precedent) => ({ ...precedent, chargement: true, erreur: null }));
    try {
      const data = await chargerRef.current();
      if (mien !== numero.current) return;
      setEtat({ data, chargement: false, erreur: null });
    } catch (error) {
      const message = await messageErreurRapport(error, "Impossible de charger ce rapport pour le moment. Réessayez.");
      if (mien !== numero.current) return;
      // Relecture automatique ratée: on garde les chiffres déjà affichés.
      if (silencieux) return;
      setEtat({ data: null, chargement: false, erreur: message });
    }
  }, []);

  useEffect(() => {
    if (!actif) {
      numero.current += 1;
      setEtat({ data: null, chargement: false, erreur: null });
      return;
    }
    // Autre mois ou autre filtre: on ne garde pas l'ancienne réponse à l'écran.
    setEtat({ data: null, chargement: true, erreur: null });
    lancer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cle, actif]);

  // Une dépense, un paiement, un trajet ou une clôture enregistrés ailleurs
  // changent les chiffres du mois: l'état se relit, l'ancien reste affiché
  // pendant la lecture (pas de page blanche).
  useRafraichirSur(['rapports'], lancer, { actif });

  return { ...etat, recharger: lancer };
};

export default useFleetReport;
